/**
 * Personalization service — thin wrapper around the personalization-context
 * projection endpoint. Consumed by the `usePersonalizationContextProjection`
 * TanStack Query hook.
 */

import { apiService } from './api.service';
import type {
  PersonalizationContextProjection,
  PersonalizedPromptPoolResponse,
  PersonalizedPromptSurface,
} from '@bower/shared';

const MAX_ONBOARDING_ARTIFACT_IDS = 20;

class PersonalizationService {
  /**
   * The acting user's typed personalization-context projection for the current
   * workspace (`GET /workspace/personalization/context`).
   */
  async getContext() {
    return apiService.get<PersonalizationContextProjection>('/workspace/personalization/context');
  }

  async getPromptPool(
    surface: PersonalizedPromptSurface,
    artifactIds: readonly string[] = [],
    generation = 0,
  ) {
    const params = new URLSearchParams({ surface });
    if (surface === 'onboarding') {
      [...new Set(artifactIds.filter(Boolean))]
        .sort()
        .slice(0, MAX_ONBOARDING_ARTIFACT_IDS)
        .forEach((artifactId) => params.append('artifactIds', artifactId));
    } else {
      params.set('generation', String(generation));
    }
    return apiService.get<PersonalizedPromptPoolResponse>(
      `/workspace/personalization/prompts?${params.toString()}`,
    );
  }
}

export const personalizationService = new PersonalizationService();
export type {
  PersonalizationContextProjection,
  PersonalizedPromptDescriptor,
  PersonalizedPromptSurface,
} from '@bower/shared';
